<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { type FortuneWheelPrizeV2 } from '@/types/shared/fortune-wheel'
import { useConfig } from '@/composables/game/config'
import {
    getHighestAffordableMultiplier,
    getMultiplierOptions,
    MULTIPLIER_MIN_SPINS,
    useFortuneWheel,
    useGoldWheel,
} from '@/composables/game/fortune-wheel'
import { syncPlayer, usePlayer } from '@/composables/game/player'
import BaseButton from '@/components/base/BaseButton.vue'
import { T, useTranslate } from '@tolgee/vue'
import { useFlyingCoins } from '@/composables/game/flying-coins'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import ExtraSpinOverlay from '@/components/overlays/ExtraSpinOverlay.vue'
import { useAnalytics } from '@/composables/analytics'
import InfoCircleIcon from '@/components/icons/InfoCircleIcon.vue'
import SpinGeneralInfoModal from '@/components/modals/SpinGeneralInfoModal.vue'
import SpinMoreInfoModal from '@/components/modals/SpinMoreInfoModal.vue'
import SpinnerIcon from '@/components/icons/SpinnerIcon.vue'
import type { ExtraSpinCounts } from '@/types/fortune-wheel'
import GoldWheelOverlay from '@/components/overlays/GoldWheelOverlay.vue'
import StreakBar from '@/components/StreakBar.vue'
import { twMerge } from 'tailwind-merge'
import FortuneWheel from '@/components/FortuneWheel.vue'
import { useConfetti } from '@/composables/game/confetti'
import WheelChestOverlay from '@/components/overlays/WheelChestOverlay.vue'
import dropeeToken from '@/assets/images/dropee-token-new.webp'
import { formatNumberFullTruncate, formatNumberSemiTruncate } from '@/helpers/utils'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import OutOfSpinsModal from '@/components/modals/OutOfSpinsModal.vue'
import FingerUpGif from '@/assets/images/gifs/finger-up.gif'
import { useOnboardingFingerEmojis } from '@/composables/game/onboarding'
import WheelPrizeOverlay from '@/components/overlays/WheelPrizeOverlay.vue'
import { LimitedQueue } from '@/helpers/queue'
import ChestConfettiImage from '@/assets/images/emoji-chest.webp'
import CoinConfettiImage from '@/assets/images/icons/gold-coin-icon-s3.webp'
import TicketConfettiImage from '@/assets/images/emojis/emoji-ticket.webp'
import FeatherConfettiImage from '@/assets/images/feather.webp'
import IdeaConfettiImage from '@/assets/images/special-currencies/idea.webp'
import HorseSpiritConfettiImage from '@/assets/images/special-currencies/horse-spirit.webp'
import FirecrackerConfettiImage from '@/assets/images/special-currencies/firecracker.webp'
import StarConfettiImage from '@/assets/images/star.png'
import { useAchievementsOverlay } from '@/composables/game/achievements'
import { useGiveaway } from '@/composables/game/giveaway'
import router from '@/router'
import { RouteName } from '@/routes'
import BaseToggle from '@/components/base/BaseToggle.vue'
import { AD_TYPE, useAd } from '@/composables/game/ad'
import FeatherIcon from '@/components/icons/FeatherIcon.vue'
import WheelSpecialCurrencyOverlay from '@/components/overlays/WheelSpecialCurrencyOverlay.vue'
import FortuneWheelImage from '@/assets/images/fortune-wheel.webp'
import StarImage from '@/assets/images/premium.png'
import MultiplierSelector from '@/components/MultiplierSelector.vue'
import MultiplierIndicatorPill from '@/components/MultiplierIndicatorPill.vue'
import PrizeMultiplierUnlockedModal from '@/components/modals/PrizeMultiplierUnlockedModal.vue'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import { parseProfitHours } from '@/helpers/profit-time'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'

const props = defineProps<{
	initFinished: boolean
}>()
const emit = defineEmits<{
	(e: 'onPrizeClaimed'): void
	(e: 'handleIsGold', value: boolean): void
	(e: 'isLoadingAd', value: boolean): void
}>()

const {
  updateState,
  availableSpins,
  canAutoSpin,
  isAutoSpin,
  isAutoSpinOnHold,
  updateAutoSpin,
} = useFortuneWheel()
const { config } = useConfig()
const { t } = useTranslate()
const { startCoinAnimation } = useFlyingCoins()
const { addHapticFeedback, addNotificationFeedback } = useHapticFeedback()
const { triggerEvent } = useAnalytics()
const { streak, isGold: showGold, completedStreakCount, isTodayClaimed } = useGoldWheel()
const { showConfetti } = useConfetti()
const { canShowOnboardingEmoji, showOnboardingEmojiWheel } = useOnboardingFingerEmojis()
const { watchedDailyAdsCount, maxDailyAdsCount, showAd } = useAd(AD_TYPE.FOR_DOUBLE_PRIZE)
const { goldenCoinValue, isBasePlatform } = usePlayer()
const { giveaway } = useGiveaway()
const isGiveawayActive = computed(() => !!giveaway.value)

type AutoSpinPrizeItem = FortuneWheelPrizeV2 & { isChest: boolean }

const autoSpinListLimit = 20
const selectedMultiplier = ref(1)
const showMultiplierPill = ref(false)
const autoSpinQueue = new LimitedQueue<AutoSpinPrizeItem>(autoSpinListLimit)
const hasSeenMultiplierUnlockedModal = useLocalStorage(buildDropeeStorageKey('has_seen_multiplier_unlocked_modal'), false)
const showMultiplierUnlockedModal = ref(false)

const showMultiplierSelector = computed(() => availableSpinsToShow.value >= MULTIPLIER_MIN_SPINS && !isGold.value)

const multiplierOptions = computed(() => getMultiplierOptions(availableSpinsToShow.value))

// The actual multiplier passed to the API
const spinMultiplier = computed(() => selectedMultiplier.value)

const isSpinning = ref<boolean>(false)
const isGold = ref<boolean>(showGold.value)
const streakToShow = ref(streak.value)
const streakCount = ref<number>(completedStreakCount.value)
const claimedToday = ref<boolean>(isTodayClaimed.value)

const pressTime = ref<ReturnType<typeof setTimeout>>()
const isLongPrimaryPress = ref<boolean>(false)
const stopAutoSpin = ref<boolean>(false)
const startedAutoSpin = ref<boolean>(false)
const newPress = ref<boolean>(false)
const pressStartTime = ref(0)
const intervalId = ref<ReturnType<typeof setInterval>>()
const currentMarginTop = ref(-8)

const showWheelPrizeOverlay = ref<boolean>(false)
const showWheelChestOverlay = ref<boolean>(false)
const showWheelSpecialCurrencyOverlay = ref<boolean>(false)
const showExtraSpinOverlay = ref<boolean>(false)
const showSpinGeneralInfoModal = ref<boolean>(false)
const showSpinMoreInfoModal = ref<boolean>(false)
const showOutOfSpinModal = ref<boolean>(false)
const showAdExtraSpinOverlay = ref<boolean>(false)
const showGoldWheelOverlay = ref<boolean>(false)
const initialConfettiShown = ref<boolean>(false)
const showFingerUpImage = computed(() => canShowOnboardingEmoji.value && showOnboardingEmojiWheel.value)

const progressIsBarMounted = ref<boolean>(false)
const fortuneWheelInitFinished = computed((): boolean => progressIsBarMounted.value)

const currentPrize = ref<FortuneWheelPrizeV2 | null>(null)
const lastAppliedMultiplier = ref(1)
const isAnimatingOut = ref(false)

const availableSpinsToShow = ref<number>(availableSpins.value)

const extraSpinDescription = t.value('fortune_wheel.extra_spin.description', 'Spin the wheel again and earn more!')

const showAdsForAutoSpin = ref(false)
const remainingAdsCount = computed(() => maxDailyAdsCount.value - watchedDailyAdsCount.value)

const spinUpgrades = computed<ExtraSpinCounts>(() => {
  return {
    referral: config.value.game.fortuneWheel.extraSpin.referral.amount,
    upgrades: config.value.game.fortuneWheel.extraSpin.upgrades.amount,
    levelUp: config.value.game.fortuneWheel.extraSpin.levelUp.amount,
    ad: {
      spinCount: config.value.game.fortuneWheel.extraSpin.watchAd.amount,
      perDayCount: config.value.game.fortuneWheel.extraSpin.watchAd.maxPerDay,
    },
  }
})

const isPrimaryButtonLoading = computed(() => (isSpinning.value && !isAutoSpin.value) || (isSpinning.value && isAutoSpin.value && stopAutoSpin.value))

const bigWinElement = ref<HTMLElement | null>()
const bigWinAnimationTimeout = ref<ReturnType<typeof setTimeout> | null>()

const isBigWin = computed(() => {
  if (!currentPrize.value) return false
  return checkBigWin(currentPrize.value)
})

function checkBigWin(_prize: FortuneWheelPrizeV2) {
  return _prize.id === 'goldenCoin_0.4'
}

function getChestPrizeLabel(prize: FortuneWheelPrizeV2): string {
  const isDoubleProfit = prize.id.includes('double_profit') || prize.currency === 'doubleProfit'
  const isOfflineProfit = prize.id.includes('offline_profit') || prize.currency === 'offlineProfit'

  if (isDoubleProfit) {
    const { hours, minutes, displayInHours } = parseProfitHours(prize.amount)
    return displayInHours ? `+${hours}H PPH x2` : `+${minutes}min PPH x2`
  }

  if (isOfflineProfit) {
    const { hours, minutes, displayInHours } = parseProfitHours(prize.amount)
    return displayInHours ? `+${hours}H Offline Profit` : `+${minutes}min Offline Profit`
  }

  // Handle other chest prize types by currency
  if (prize.currency === 'spin') {
    return `${prize.amount} spin${prize.amount > 1 ? 's' : ''}`
  }

  if (prize.currency === 'goldenCoin') {
    return `${formatNumberFullTruncate(goldenCoinValue.value * prize.amount)} coins`
  }

  if (prize.currency === 'giveawayTicket') {
    return `${prize.amount} ${prize.amount === 1 ? 'Lucky Rush ticket' : 'Lucky Rush tickets'}`
  }

  // Check if it's a special currency from allSpecialCurrenciesConfig (includes live ops currencies)
  const specialCurrency = allSpecialCurrenciesConfig.value[prize.currency]
  if (specialCurrency) {
    return `${prize.amount} ${specialCurrency.label}`
  }

  // Default fallback
  return 'Prize'
}

function getChestPrizeImage(prize: FortuneWheelPrizeV2): string | undefined {
  if (prize.currency === 'goldenCoin') return CoinIcon
  if (prize.currency === 'spin') return FortuneWheelImage
  if (prize.currency === 'giveawayTicket') return TicketConfettiImage
  if (prize.currency === 'doubleProfit' || prize.id.includes('double_profit')) return undefined
  if (prize.currency === 'offlineProfit' || prize.id.includes('offline_profit')) return undefined

  // Check if it's a special currency from allSpecialCurrenciesConfig (includes live ops currencies)
  const specialCurrency = allSpecialCurrenciesConfig.value[prize.currency]
  if (specialCurrency) {
    return specialCurrency.image
  }
  return undefined
}

const prizeShapeImageMap: Record<string, string> = {
  points: CoinConfettiImage,
  goldenCoin: CoinConfettiImage,
  dropee: dropeeToken,
  giveawayTicket: TicketConfettiImage,
  goldFeather: FeatherConfettiImage,
  idea: IdeaConfettiImage,
  horseSpirit: HorseSpiritConfettiImage,
  firecracker: FirecrackerConfettiImage,
  star: StarConfettiImage,
}

function getConfettiShapes(prize: FortuneWheelPrizeV2) {
  const src = prizeShapeImageMap[prize.currency]
  if (!src) return undefined
  return {
    count: 40,
    decay: 0.9,
    shapes: ['image'],
    shapeOptions: {
      'image': [
        {
          'src': src,
          'width': 24,
          'height': 24,
          'particles': {
            'size': {
              'value': 12,
            },
          },
        },
      ],
    },
  }
}

// Check if currency should use the special currency overlay
// Excludes: goldenCoin, spin, star (use WheelPrizeOverlay)
// Excludes: chest prizes (use WheelChestOverlay)
function isSpecialCurrency(currency: string): boolean {
  // List of currencies that should NOT use the special currency overlay
  const excludedCurrencies = ['goldenCoin', 'spin', 'star', 'points', 'dropee']

  if (excludedCurrencies.includes(currency)) {
    return false
  }

  // Check if it's in our hardcoded special currencies list
  const specialCurrencies = ['goldFeather', 'idea', 'horseSpirit', 'firecracker', 'giveawayTicket']
  if (specialCurrencies.includes(currency)) {
    return true
  }

  // Check if it's a live ops currency from config
  return currency in allSpecialCurrenciesConfig.value
}

async function onPrize(prize: FortuneWheelPrizeV2, isChest: boolean = false) {
  if (isAutoSpin.value && prize.id === 'goldenCoin_0.4' && showAdsForAutoSpin.value) {
    isAutoSpinOnHold.value = true
    await showAd()
    isAutoSpinOnHold.value = false
  }
  if (isAutoSpin.value) {
    onHidePrizeOverlay()
  } else {
    if (isChest) {
      showWheelChestOverlay.value = true
    } else if (isSpecialCurrency(prize.currency)) {
      showWheelSpecialCurrencyOverlay.value = true
    } else {
      showWheelPrizeOverlay.value = true
    }
  }
  setTimeout(() => {
    if (prize.currency === 'points' && !isAutoSpin.value) {
      startCoinAnimation()
    } else {
      if (!isAutoSpin.value) {
        showConfetti(undefined)
      } else {
        showConfetti({ position: { x: 50, y: 16 }, ...getConfettiShapes(prize), count: 30 })
      }
    }
  }, 300)
}

function onAdditionalSpin() {
  showExtraSpinOverlay.value = true
}

function onWheelStopped(prize: FortuneWheelPrizeV2 | null, prizeMultiplier: number = 1, isChest: boolean = false) {
  currentPrize.value = prize
  lastAppliedMultiplier.value = prizeMultiplier
  if (prize) {
    autoSpinQueue.push({ ...prize, isChest })
  }
  startedAutoSpin.value = false
  isSpinning.value = false
  addNotificationFeedback('success')
  if (prize) {
    triggerEvent({ event: `win_prize_${prize.currency}`, value: prize.amount })
  }
  setTimeout(() => {
    if (prize) {
      // If it's a chest prize, always show chest overlay regardless of currency
      if (isChest) {
        onPrize(prize, isChest)
      } else if (prize.currency === 'spin' && !isAutoSpin.value) {
        onAdditionalSpin()
      } else if (prize.currency !== 'spin_golden') {
        onPrize(prize, isChest)
      }
    }
    availableSpinsToShow.value = availableSpins.value
  }, isAutoSpin.value ? 0 : 100)
}

async function doSpin() {
  if (availableSpinsToShow.value || isGold.value) {
    isSpinning.value = true
    if (!isAutoSpin.value) {
      availableSpinsToShow.value -= spinMultiplier.value
    }
  }
}

function onHidePrizeOverlay() {
  showWheelPrizeOverlay.value = false
  showWheelChestOverlay.value = false
  showWheelSpecialCurrencyOverlay.value = false
  showExtraSpinOverlay.value = false
  if (!isSpinning.value && currentPrize.value?.currency === 'dropee') {
    emit('onPrizeClaimed')
  }

  setTimeout(() => {
    if (!showWheelPrizeOverlay.value && !showWheelChestOverlay.value && !showWheelSpecialCurrencyOverlay.value && !showExtraSpinOverlay.value) {
      currentPrize.value = null
      isAnimatingOut.value = false
    }
  }, isAutoSpin.value ? 2000 : 0)

  if (showGold.value) {
    showGoldWheelOverlay.value = true
  } else {
    if (!availableSpins.value) {
      showOutOfSpinModal.value = true
    }
  }
  streakToShow.value = streak.value
  streakCount.value = completedStreakCount.value
  claimedToday.value = isTodayClaimed.value
  emit('handleIsGold', isGold.value)
  isGold.value = showGold.value

  if (isAutoSpin.value) {
    setTimeout(() => {
      isAnimatingOut.value = true
    }, 50) // Duration matches scaleOut animation
    setTimeout(() => {
      doSpin()
    }, 50)
  }
}

function onHideGoldWheelOverlay() {
  showGoldWheelOverlay.value = false
  isGold.value = showGold.value
}

async function purchaseDone() {
  await showConfetti()
  await syncPlayer()
  await updateState()
}

function onPrimaryClick() {
  showOnboardingEmojiWheel.value = false
  if (isGold.value) {
    doSpin()
    return
  }
  if (!canAutoSpin.value) {
    pressStartTime.value = Date.now()
    startPressAnimation()
    if (!availableSpinsToShow.value) {
      showOutOfSpinModal.value = true
    } else {
      doSpin()
    }
    setTimeout(() => {
      clearInterval(intervalId.value)
      currentMarginTop.value = -8
    }, 100)
  } else if (isAutoSpin.value && newPress.value) {
    stopAutoSpin.value = true
  }
}

function startPressAnimation() {
  intervalId.value = setInterval(() => {
    const elapsed = Date.now() - pressStartTime.value
    const progress = Math.min(elapsed / 100, 1) // Cap progress at 1

    currentMarginTop.value = -8 + 8 * progress

    if (progress === 1) {
      clearInterval(intervalId.value)
    }
  }, 50)
}

function onPrimaryPress() {
  showOnboardingEmojiWheel.value = false
  if (canAutoSpin.value && !isAutoSpin.value) {
    newPress.value = false
    pressStartTime.value = Date.now()
    startPressAnimation() // Start animation
    pressTime.value = setTimeout(() => {
      isLongPrimaryPress.value = true
      updateAutoSpin(true)
      autoSpinQueue.clear()
      startedAutoSpin.value = true
      addHapticFeedback('heavy')
      setTimeout(() => {
        doSpin()
      }, 200)
    }, 800)
    setTimeout(() => {
      if (pressTime.value) {
        addHapticFeedback('medium')
      }
    }, 100)
  }
}

function onPrimaryRelease() {
  if (canAutoSpin.value && !isAutoSpin.value) {
    clearTimeout(pressTime.value)
    if (!isLongPrimaryPress.value) {
      setTimeout(() => {
        clearInterval(intervalId.value)
        currentMarginTop.value = -8
      }, 100)
      doSpin()
    }
  }
  setTimeout(() => {
    newPress.value = true
  }, 300)
}

function checkForInitialConfetti() {
  if (showFingerUpImage.value && !initialConfettiShown.value) {
    initialConfettiShown.value = true
    showConfetti()
  }
}

onMounted(() => {
  checkForInitialConfetti()
})

watch(showFingerUpImage, () => {
  checkForInitialConfetti()
})

watch(availableSpinsToShow, (spins) => {
  // 0 spins — stop everything
  if (spins < MULTIPLIER_MIN_SPINS) {
    if (isAutoSpin.value) {
      stopAutoSpin.value = true
    }
    selectedMultiplier.value = 1
    return
  }

  const currentOptions = getMultiplierOptions(spins)

  // Current multiplier is still valid and affordable — keep it
  if (currentOptions.includes(selectedMultiplier.value) && spins >= selectedMultiplier.value) {
    return
  }

  // Auto-downgrade to highest affordable multiplier in current tier
  selectedMultiplier.value = getHighestAffordableMultiplier(spins)
})

watch([availableSpins, isSpinning], () => {
  if (!isSpinning.value) {
    availableSpinsToShow.value = availableSpins.value
  }
})

watch(() => props.initFinished, () => {
  if (props.initFinished) {
    availableSpinsToShow.value = availableSpins.value
    streakToShow.value = streak.value
    streakCount.value = completedStreakCount.value
    claimedToday.value = isTodayClaimed.value
    isGold.value = showGold.value

    // Show multiplier unlocked modal on page load if user has enough spins
    if (availableSpins.value >= MULTIPLIER_MIN_SPINS && !hasSeenMultiplierUnlockedModal.value && !showGold.value) {
      showMultiplierUnlockedModal.value = true
    }
  }
})

watch([isSpinning, stopAutoSpin], () => {
  if (!isSpinning.value && stopAutoSpin.value) {
    updateAutoSpin(false)
    autoSpinQueue.clear()
    stopAutoSpin.value = false
    isLongPrimaryPress.value = false
    currentMarginTop.value = -8
  }
})

watch(showGold, () => {
  if (showGold.value) {
    stopAutoSpin.value = true
  }
})

// Show multiplier pill when user selects a multiplier > 1
watch(selectedMultiplier, (multiplier) => {
  if (multiplier > 1) {
    showMultiplierPill.value = true
    // Hide after a short display
    setTimeout(() => {
      showMultiplierPill.value = false
    }, 1600)
  }
})

watch(isBigWin, (value) => {
  setTimeout(() => {
    if (!value || !bigWinElement.value || !isAutoSpin.value) return
    if (bigWinAnimationTimeout.value) {
      clearTimeout(bigWinAnimationTimeout.value)
    }

    bigWinElement.value.classList.remove('hidden')
    bigWinElement.value.classList.add('visible')

    // Hide the text after 1.5s (duration of animation)
    bigWinAnimationTimeout.value = setTimeout(() => {
      if (!bigWinElement.value) return
      bigWinElement.value.classList.remove('visible')
      bigWinElement.value.classList.add('hidden')
    }, 1000)
  }, 50)
})

function triggerAd() {
  // @ts-ignore
  window.TelegramAdsController.triggerNativeNotification().catch((result: string) => {
    console.log(result)
  })
}

const { isAchievementsOverlayDelayed } = useAchievementsOverlay()

watch([isSpinning, currentPrize], ([isSpinning, currentPrize]) => {
  if (isAutoSpin.value) return
  if (isSpinning) {
    isAchievementsOverlayDelayed.value = true
    return
  }
  isAchievementsOverlayDelayed.value = !!currentPrize
})

watch(isAutoSpin, isAutoSpin => {
  if (isAutoSpin) {
    isAchievementsOverlayDelayed.value = true
    // triggerAd()
    return
  }
})
</script>
<template>
  <div
    class="flex-col relative flex min-h-0 flex-1 w-full"
  >
    <template v-if="isAutoSpin && !startedAutoSpin">
      <div
        class="absolute w-full p-y-1.5 -top-[46px] flex flex-col z-[93] max-h-[90%] h-full overflow-hidden transition-all duration-[300ms] rounded-t-lg"
        :style="{height: `${(Math.min(autoSpinQueue.size(), autoSpinListLimit) + 1) * 50}px`}"
      >
        <div
          v-for="(prize, index) in autoSpinQueue.getQueue()"
          :key="prize.index"
          :class="index === 0 ? 'queue-item': 'mb-3'"
        >
          <div
            class="gold-background flex items-center justify-center rounded-lg text-black text-base font-bold h-[38px] min-h-[38px] w-full relative overflow-visible"
            :class="index === 0 && 'queue-item-container'"
          >
            <div
              v-if="checkBigWin(prize.data)"
              class="flex items-center justify-center h-6 absolute left-2.5 top-1/2 translate-and-rotate z-[94] overflow-visible"
            >
              <span
                :class="twMerge('text-black font-semibold transition-all duration-300',
                                index === 0 ? 'leading-4 white-stroke-thin scale-up' : 'text-xl leading-6 white-stroke-thin',
                )"
              >
                BIG WIN!
              </span>
            </div>
            <div
              v-if="prize.data.currency === 'dropee'"
              :class="twMerge('absolute flex items-center justify-center size-8 z-[94] transition-all duration-400 top-1/2 left-2.5 translate-and-rotate overflow-visible')"
            >
              <img
                src="@/assets/images/emoji-money-eye.png"
                :class="twMerge(index === 0 ? 'scale-up-small' : 'size-full')"
              >
            </div>
            <div
              v-if="prize.data.isChest"
              class="flex items-center gap-x-1"
            >
              <img
                :src="ChestConfettiImage"
                class="h-4 w-auto"
              >
              <img
                v-if="getChestPrizeImage(prize.data)"
                :src="getChestPrizeImage(prize.data)"
                class="h-4 w-auto object-cover"
              >
              <span>{{ getChestPrizeLabel(prize.data) }}</span>
            </div>
            <div
              v-else-if="prize.data.currency === 'dropee'"
              class="flex items-center justify-center gap-x-1"
            >
              <img
                :src="dropeeToken"
                class="size-4"
              >
              <span>{{ `+${formatNumberFullTruncate(prize.data.amount / 100)} $DROPEE` }}</span>
            </div>
            <div
              v-else-if="prize.data.currency === 'star'"
              class="flex items-center justify-center gap-x-1"
            >
              <img
                :src="StarImage"
                class="size-4"
              >
              <span>{{ `+${prize.data.amount} stars` }}</span>
            </div>
            <div
              v-else-if="prize.data.currency === 'spin'"
              class="flex items-center justify-center gap-x-1"
            >
              <img
                :src="FortuneWheelImage"
                class="size-4"
              >
              <span>{{ `+${prize.data.amount} spin${prize.data.amount > 1 ? 's' : ''}` }}</span>
            </div>
            <div
              class="flex items-center justify-center gap-x-1"
              v-else-if="prize.data.currency === 'goldenCoin'"
            >
              <img
                :src="CoinIcon"
                class="h-4"
              >
              <span>{{
                `+${formatNumberFullTruncate((showAdsForAutoSpin && prize.data.id === 'goldenCoin_0.4' ? 2 : 1) * goldenCoinValue * prize.data.amount)}`
              }}</span>
            </div>
            <div
              v-else-if="prize.data.currency === 'goldFeather'"
              class="flex items-center justify-center gap-x-1"
            >
              <FeatherIcon
                class="size-4 rotate-[30deg]"
              />
              <span>{{ `${prize.data.amount} Gold Feathers` }}</span>
            </div>
            <div
              v-else-if="prize.data.currency === 'idea'"
              class="flex items-center justify-center gap-x-1"
            >
              <img
                class="size-4 object-cover"
                src="@/assets/images/special-currencies/idea.webp"
              >
              <span>{{ `${prize.data.amount} Ideas` }}</span>
            </div>
            <div
              v-else-if="prize.data.currency === 'horseSpirit'"
              class="flex items-center justify-center gap-x-1"
            >
              <img
                class="size-4 object-cover"
                src="@/assets/images/special-currencies/horse-spirit.webp"
              >
              <span>{{ `${prize.data.amount} Horse Spirits` }}</span>
            </div>
            <div
              v-else-if="prize.data.currency === 'firecracker'"
              class="flex items-center justify-center gap-x-1"
            >
              <img
                class="size-4 object-cover"
                src="@/assets/images/special-currencies/firecracker.webp"
              >
              <span>{{ `${prize.data.amount} Firecrackers` }}</span>
            </div>
            <div
              v-else-if="prize.data.currency === 'giveawayTicket'"
              class="flex items-center gap-x-1"
            >
              <img
                src="@/assets/images/emojis/emoji-ticket.png"
                class="h-4 w-auto"
              >
              <span>
                <T
                  key-name="fortune_wheel.auto_spin.giveaway_ticket"
                  default-value="{count, plural, one {# Lucky Rush ticket} other {# Lucky Rush tickets}}"
                  :params="{count: prize.data.amount}"
                />
              </span>
            </div>
            <div
              v-else
              class="flex items-center gap-x-1"
            >
              <img
                v-if="getChestPrizeImage(prize.data)"
                :src="getChestPrizeImage(prize.data)"
                class="h-4 w-auto object-cover"
              >
              <img
                v-else
                :src="ChestConfettiImage"
                class="h-4 w-auto"
              >
              <span>{{ getChestPrizeLabel(prize.data) }}</span>
            </div>
            <span
              v-if="selectedMultiplier > 1"
              class="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/[.87] text-sm font-bold"
            >
              (x{{ prize.data.currency === 'dropee' ? 1 : selectedMultiplier }})
            </span>
          </div>
        </div>
      </div>
      <div class="absolute h-[90%] w-full z-[93] autospin-shadow" />
    </template>
    <InfoCircleIcon
      class="text-white/[38%] size-6 z-[3] absolute right-0 -top-1"
      @click="showSpinGeneralInfoModal = true"
    />
    <div
      class=" min-[420px]:mt-6 w-full flex flex-1 justify-center relative mb-4 min-[380px]:mb-6"
    >
      <div
        v-if="isAutoSpin"
        class="fixed h-screen w-screen  inset-0 z-[91] bg-black/20 backdrop-blur-sm"
        aria-hidden="true"
      />
      <div
        :class="twMerge('flex flex-1 w-full flex-col items-center gap-y-4 min-[380px]:gap-y-8')"
      >
        <div class="relative w-full flex justify-center">
          <MultiplierIndicatorPill
            :show="showMultiplierPill"
            :multiplier="selectedMultiplier"
          />
          <FortuneWheel
            :is-spinning="isSpinning"
            :is-gold="isGold"
            :is-auto-spin="isAutoSpin"
            :init-finished="fortuneWheelInitFinished"
            :spin-multiplier="spinMultiplier"
            :is-giveaway-active="isGiveawayActive"
            @stopped="onWheelStopped"
          />
        </div>
        <div
          v-if="initFinished"
          class="flex w-full items-center justify-center relative"
        >
          <StreakBar
            :streak="streakToShow"
            :all-done="isGold"
            :blur="isGold"
            :completed-streak-count="streakCount"
            :is-today-claimed="claimedToday"
            @loaded="progressIsBarMounted = true"
          />
        </div>
      </div>
    </div>
    <div class="w-full">
      <div
        class="flex flex-col w-full items-center text-sm text-white/[.87] font-bold text-center"
        v-if="initFinished"
      >
        <p
          v-if="isAutoSpin"
        >
          <T
            key-name="fortune_wheel.auto_spin.stop"
            default-value="Press to stop auto-spin"
          />
        </p>
        <p v-else-if="canAutoSpin">
          <T
            key-name="fortune_wheel.auto_spin.hold"
            default-value="Hold to activate auto-spin"
          />
        </p>
        <div
          :class="twMerge('bg-red-dark scale-in transition-all w-full rounded-xl mt-3 h-[60px] relative', isAutoSpin ? 'z-[94]' : 'z-[1]', isGold && 'bg-transparent', showMultiplierSelector && 'mx-[20px] w-[calc(100%-40px)]')"
        >
          <BaseButton
            :style-type="isGold ? 'gold' : 'accent'"
            :class="twMerge('w-full -mt-2 transition-all duration-0 absolute top-0', isAutoSpin && 'mt-0')"
            :button-class="twMerge(isAutoSpin ? 'z-[92]' : '', isAutoSpin && '!bg-red-dark !border-red-accent', isPrimaryButtonLoading && 'pointer-events-none')"
            :loading="isPrimaryButtonLoading"
            v-touch:press="onPrimaryPress"
            v-touch:release="onPrimaryRelease"
            :has-shining="!isSpinning && !!availableSpinsToShow"
            :style="{ marginTop: `${isGold ? 0 : currentMarginTop}px` }"
            @click="onPrimaryClick"
          >
            <span
              class="flex items-center"
            >
              <span class="text-xl">

                <T
                  v-if="!isAutoSpin"
                  key-name="fortune_wheel.spin"
                  default-value="Spin"
                />
                <T
                  v-else
                  key-name="fortune_wheel.stop"
                  default-value="Stop"
                />
              </span>
              <span
                v-if="!isGold"
                class="text-lg leading-6 font-semibold ml-[8px] text-white/[87%]"
              >
                ({{ formatNumberSemiTruncate(availableSpinsToShow) }})
              </span>
            </span>
          </BaseButton>
          <MultiplierSelector
            v-if="showMultiplierSelector"
            v-model="selectedMultiplier"
            :available-spins="availableSpinsToShow"
            :disabled="isSpinning"
            :is-pressed="isAutoSpin"
            class="absolute -top-5 -right-4 z-[95]"
          />
          <img
            v-if="showFingerUpImage"
            class="pointer-events-none absolute top-0 left-1/2 translate-x-[calc(-50%+30px)] size-[120px] rotate-[-36deg] object-cover"
            :src="FingerUpGif"
          >
          <div
            v-if="isAutoSpin && !isBasePlatform"
            class="absolute left-0 bottom-0 translate-y-full z-[94] flex items-center gap-x-3 pt-3"
          >
            <BaseToggle
              :is-active="!!remainingAdsCount && selectedMultiplier === 1 ? showAdsForAutoSpin : false"
              :disabled="selectedMultiplier > 1"
              @on-toggle="selectedMultiplier === 1 && (showAdsForAutoSpin = !showAdsForAutoSpin)"
            />
            <p class="text-sm font-medium text-white/[87%] text-left w-full">
              <template v-if="selectedMultiplier > 1">
                <T
                  key-name="spin.auto_spin_ads_unavailable_multiplier"
                  default-value="Ads are unavailable while Prize Multiplier is active."
                />
              </template>
              <template v-else>
                <T
                  key-name="spin.auto_spin_watch_ad_description"
                  default-value="Turn on to double your biggest Dropee coin rewards with ads ({count} left for today)"
                  :params="{count: remainingAdsCount}"
                />
              </template>
            </p>
          </div>
        </div>
        <BaseButton
          v-if="!isGold"
          style-type="secondary"
          class="w-full scale-in min-[380px]:mt-1 relative"
          button-class="border-0 py-2 min-[380px]:py-3 h-[52px] min-[380px]:h-[60px]"
          @click="showSpinMoreInfoModal = true"
        >
          <span class="text-xl">
            <T
              key-name="fortune_wheel.get_more_spin"
              default-value="Get more spins"
            />
          </span>
        </BaseButton>
      </div>
      <div
        v-else
        class="mb-3 flex w-full justify-center"
      >
        <SpinnerIcon
          class="h-6 w-6 min-w-[24px] text-white/[60%]"
        />
      </div>
    </div>
  </div>

  <WheelPrizeOverlay
    v-if="currentPrize && showWheelPrizeOverlay"
    :show="showWheelPrizeOverlay"
    :prize="currentPrize"
    :has-spin-multiplier="spinMultiplier > 1"
    :applied-multiplier="lastAppliedMultiplier"
    @cta-click="onHidePrizeOverlay"
    @ad-watched="startCoinAnimation"
    @is-loading-ad="$emit('isLoadingAd', $event)"
  />
  <WheelSpecialCurrencyOverlay
    v-if="showWheelSpecialCurrencyOverlay && currentPrize"
    :show="showWheelSpecialCurrencyOverlay"
    :prize="currentPrize"
    :applied-multiplier="lastAppliedMultiplier"
    :show-check-entries-button="currentPrize.currency === 'giveawayTicket'"
    @hide="onHidePrizeOverlay"
    @on-check-entries="router.push({name:RouteName.GIVEAWAY})"
  />
  <WheelChestOverlay
    v-if="showWheelChestOverlay && currentPrize"
    :show="showWheelChestOverlay"
    :prize="currentPrize"
    :applied-multiplier="lastAppliedMultiplier"
    @cta-click="onHidePrizeOverlay"
    @on-check-entries="router.push({name:RouteName.GIVEAWAY})"
  />
  <ExtraSpinOverlay
    :show="showExtraSpinOverlay"
    :spin-count="currentPrize?.amount ?? 0"
    :description="extraSpinDescription"
    @close="onHidePrizeOverlay"
  />
  <ExtraSpinOverlay
    :show="showAdExtraSpinOverlay"
    :spin-count="spinUpgrades.ad.spinCount"
    :description="extraSpinDescription"
    @close="showAdExtraSpinOverlay = false"
  />
  <GoldWheelOverlay
    v-if="showGoldWheelOverlay"
    :show="showGoldWheelOverlay"
    @click="onHideGoldWheelOverlay"
  />
  <SpinGeneralInfoModal
    v-if="showSpinGeneralInfoModal"
    :show="showSpinGeneralInfoModal"
    centered
    @hide="showSpinGeneralInfoModal = false"
  />
  <SpinMoreInfoModal
    v-if="showSpinMoreInfoModal"
    :show="showSpinMoreInfoModal"
    centered
    :spin-upgrades="spinUpgrades"
    @hide="showSpinMoreInfoModal = false"
    @buy-spins="showSpinMoreInfoModal = false; router.push({name: RouteName.SHOP})"
    @on-ad-watched="showAdExtraSpinOverlay = true"
  />
  <OutOfSpinsModal
    :show="showOutOfSpinModal"
    :spin-upgrades="spinUpgrades"
    @hide="showOutOfSpinModal = false"
    @purchase-done="purchaseDone"
  />
  <PrizeMultiplierUnlockedModal
    v-if="showMultiplierUnlockedModal"
    :show="showMultiplierUnlockedModal"
    @hide="showMultiplierUnlockedModal = false"
    @do-not-show-again="hasSeenMultiplierUnlockedModal = true"
  />
</template>
<style scoped>
.gold-background {
	background: linear-gradient(90deg, #FFF246 0%, #E5B80B 37%, #FFF571 68.5%, #E5B80B 100%);
}

.scale-in-image {
	animation: scale-in-image 0.4s ease-out;
	filter: drop-shadow(0 12px 24px rgba(0, 0, 0, 0.7));
}

.scale-out {
	animation: scaleOut 0.1s forwards;
}

@keyframes scale-in-image {
	0% {
		opacity: 0;
		transform: scale(0);
	}
	20% {
		opacity: 1; /* Ensure it starts becoming visible */
	}
	70% {
		transform: scale(1.1);
	}
	100% {
		transform: scale(1);
	}
}

@keyframes scaleOut {
	from {
		transform: scale(1);
		opacity: 1;
	}
	to {
		transform: scale(0.8);
		opacity: 0;
	}
}

@keyframes slideIn {
	0% {
		transform: translateY(-100%); /* Start above the container */
	}
	100% {
		transform: translateY(0); /* End at the normal position */
	}
}

/* Apply the animation to each item */
.queue-item-container {
	transform: translateY(-100%);
	animation: slideIn 0.15s forwards;
	animation-delay: 0.15s;
}

.queue-item {
	height: 0;
	animation: growQueueItem 0.1s forwards;
	animation-delay: 0.1s;
	margin-bottom: 0;
}

/* Slide-in animation */
@keyframes growQueueItem {
	0% {
		height: 0;
		margin-bottom: 0;
	}
	100% {
		height: 38px;
		min-height: 38px;
		margin-bottom: 12px;
	}
}

.autospin-shadow {
	background: linear-gradient(to bottom,
	transparent 0%,
	transparent 60%,
	rgba(0, 0, 0, 1) 100%);
}

.translate-and-rotate {
	transform: rotate(-8deg) translateY(-50%);
}

.scale-up {
	animation: scale-up 0.3s ease infinite alternate;
	animation-delay: 0.3s;
}

@keyframes scale-up {
	from {
		font-size: 14px;
		line-height: 16px;
	}
	to {
		font-size: 20px;
		line-height: 24px;
	}
}

.scale-up-small {
	animation: scale-up-small 0.3s ease infinite alternate;
	animation-delay: 0.3s;
}

@keyframes scale-up-small {
	from {
		width: 24px;
		height: 24px;
	}
	to {
		width: 32px;
		height: 32px;
	}
}

.white-stroke {
	text-shadow: -1.5px -1.5px 0 #fff,
	1.5px -1.5px 0 #fff,
	-1.5px 1.5px 0 #fff,
	1.5px 1.5px 0 #fff,
	-2px 0 0 #fff,
	2px 0 0 #fff,
	0 -2px 0 #fff,
	0 2px 0 #fff,
	-1px -1px 0 #fff, /* Finer shadow adjustments */ 1px -1px 0 #fff,
	-1px 1px 0 #fff,
	1px 1px 0 #fff;
}

.white-stroke-thin {
	text-shadow: -1px -1px 0 #fff,
	1px -1px 0 #fff,
	-1px 1px 0 #fff,
	1px 1px 0 #fff,
	-1.5px 0 0 #fff,
	1.5px 0 0 #fff,
	0 -1.5px 0 #fff,
	0 1.5px 0 #fff,
	-0.5px -0.5px 0 #fff, /* Finer shadow adjustments */ 0.5px -0.5px 0 #fff,
	-0.5px 0.5px 0 #fff,
	0.5px 0.5px 0 #fff;
}
</style>
